import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ScrollArea } from '@deepguide-ai/ui/scroll-area';
import { Button } from '@deepguide-ai/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@deepguide-ai/ui/dialog';
import { queryClient, trpc } from '@/lib/trpc';
import { vendorLogoPath } from '@/lib/vendor-logo';
import type { ConfigModalProps, IntegrationProvider } from '../types';
import { openOAuthConnectPopup } from '../oauth-connect-lifecycle';

function HubSpotConfigModal({ open, onOpenChange, integration, workspaceId, onComplete }: ConfigModalProps) {
  const [connecting, setConnecting] = useState(false);
  const integrationId = integration?.id ?? '';
  const status = useQuery({ ...trpc.integrations.getHubSpotConnectionStatus.queryOptions({ integrationId }), enabled: open && !!integrationId });
  return <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className='sm:max-w-xl'>
      <DialogHeader>
        <DialogTitle>HubSpot evidence</DialogTitle>
        <DialogDescription>Collect account access, security activity and login history for compliance evidence.</DialogDescription>
      </DialogHeader>
      <ScrollArea type='scroll' className='max-h-[65vh]' viewClassName='max-h-[65vh] [&>div]:!block'>
      <div className='space-y-3 px-6 pb-4 text-sm'>
        {integrationId && status.isLoading ? <p role='status'>Loading HubSpot connection…</p> : null}
        {status.error ? <div className='space-y-2'>
          <p role='alert' className='text-destructive'>{status.error.message}</p>
          <Button variant='outline' disabled={status.isFetching} onClick={() => void status.refetch()}>Retry loading</Button>
        </div> : status.data ? <dl className='space-y-1'><div><dt className='text-muted-foreground'>Connected account</dt><dd>{status.data.providerAccount}</dd></div></dl> : null}
        <p className='text-muted-foreground'>Use Scan on the HubSpot card to collect evidence and view progress and results. Use Access reviews to review collected accounts. Connecting alone does not collect evidence.</p>
        <p className='text-muted-foreground'>Inventory includes users, reported super-admin flags, permission sets and direct team membership. Unresolved role or team references require review before replacing an access-review population. MFA, SSO and account lifecycle are unknown; permission-set names do not establish effective permissions.</p>
        <p className='text-muted-foreground'>A platform operator must configure the HubSpot OAuth app before connecting. Consent grants read access to users, teams and account security activity. Existing connections must reconnect after the app’s security-activity permission is approved. CRM contacts, deals and marketing content are not collected.</p>
        <p className='text-muted-foreground'>Login history includes available successful and failed attempts from the past 90 days. Events do not prove monitoring, retention or human review. Missing actor or origin fields need separate evidence. Available security activity includes user and authentication-setting changes; those events do not establish current MFA or SSO policy.</p>
        {status.data?.lastError ? <p role='alert' className='text-destructive'>{status.data.lastError}</p> : null}
        <p className='text-muted-foreground'>Disconnect from the integration card to stop future collection. Collected evidence is retained. Revoke app access in HubSpot separately.</p>
      </div>
      </ScrollArea>
      <DialogFooter>
        <Button disabled={connecting} onClick={async () => {
          setConnecting(true);
          try { await HubSpotProvider.handleConnect({ workspaceId, onSuccess: onComplete, onFinish: () => setConnecting(false) }); }
          catch { setConnecting(false); }
        }}>{connecting ? 'Connecting…' : integrationId ? 'Reconnect HubSpot' : 'Connect HubSpot'}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>;
}

export const HubSpotProvider: IntegrationProvider = {
  id: 'HubSpot', name: 'HubSpot', description: 'Account users, permission sets, teams and security/login history',
  Icon: () => <img src={vendorLogoPath('hubspot')} alt='' className='size-6 object-contain' />,
  ConfigModal: HubSpotConfigModal, postAuthAction: 'CONFIGURE',
  getFeatures: () => [
    { id: 'access', name: 'Access inventory', description: 'Read users, permission-set assignments and direct teams', enabled: true },
    { id: 'audit', name: 'Security and login history', description: 'Read available security changes and 90-day login activity', enabled: true },
  ],
  handleConnect: async ({ onSuccess, onFinish }) => {
    const result = await queryClient.getMutationCache().build(queryClient, trpc.integrations.initHubSpotAuth.mutationOptions()).execute(undefined).catch(error => {
      toast.error(error instanceof Error ? error.message : 'Could not start HubSpot authorization');
      onFinish?.('error');
      throw error;
    });
    openOAuthConnectPopup({ authUrl: result.authUrl, windowName: 'hubspot-auth', provider: 'HubSpot', onSuccess, onFinish,
      onError: message => toast.error(message), onPopupBlocked: () => toast.error('Allow popups to connect HubSpot.'),
    });
  },
  onConnectComplete: async ({ code, state }) => {
    const result = await queryClient.getMutationCache().build(queryClient, trpc.integrations.completeHubSpotAuth.mutationOptions()).execute({ code, state });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.integrations.getHubSpotConnectionStatus.queryKey() }),
    ]);
    return result;
  },
  syncData: async () => { throw new Error('Use Scan on the HubSpot integration card to collect access evidence.'); },
};
